import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import reactStringReplace from 'react-string-replace'

import { validateEmail } from 'utils/email-validator'

import { fonts, mediaQueries, palette } from '../../styles/variables'
import { CloseButton, PrimaryButton } from '../buttons/Buttons'
import { Text } from '../topography'
import { UberInputField } from '../input/InputField'
import { translate } from '../../utils/translation'
import { UberTextarea } from '../textarea/Textarea'
import { PhoneInput } from '../input/PhoneInput'
import { validatePhoneLib } from '../../utils/inputValidation'
import { inquiryCustomPlan } from '../../utils/api'
import { TableNotification } from '../tables/styled'

import Modal from './Modal'

const Wrapper = styled.div({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  padding: '40px 40px 24px 40px',
  overflowY: 'visible',
  [mediaQueries.mobile]: {
    minHeight: '50vh',
  },
})

const desktopStyle = {
  height: 'fit-content',
  maxHeight: '90vh',
  overflow: 'auto',
  maxWidth: '784px',
  width: '100%',
}
const mobileStyle = {
  width: '100%',
  height: '100vh',
  borderRadius: 0,
}

const Form = styled.div({
  margin: '32px 0 40px 0',
  display: 'flex',
  gap: '24px',
  flexDirection: 'column',
})

const Row = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: '24px',
  [mediaQueries.mobile]: {
    flexDirection: 'column',
  },
})

const Footer = styled.div({
  marginTop: '84px',
  borderTop: `1px solid ${palette.gray30}`,
  padding: '16px 0 0 0',
  display: 'flex',
  justifyContent: 'center',
})

const Input = (props) => {
  return (
    <UberInputField
      styles={{ flexGrow: 1, flexBasis: 0 }}
      height="50px"
      backgroundColor={palette.lightGray10}
      placeholderColor={palette.primaryTint}
      focusedBorderColor={palette.orange}
      {...props}
    />
  )
}

const initData = {
  name: '',
  company_name: '',
  email: '',
  phone_number: '',
  message: '',
}

export default ({ isOpen, onClose, onSubmit, ...rest }) => {
  const [data, setData] = useState({ ...initData })
  const [isValid, setIsValid] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [serverError, setServerError] = useState(null)

  useEffect(() => {
    if (
      !!data.name &&
      !!data.company_name &&
      !!data.email &&
      !!data.message &&
      (!isSubmitted || validate())
    ) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }
    // eslint-disable-next-line
  }, [data])

  const validate = () => {
    return (
      validateEmail(data.email) &&
      (data.phone_number === '' || validatePhoneLib(data.phone_number))
    )
  }

  const handleSubmit = () => {
    setIsSubmitted(true)
    setServerError(null)
    if (validate()) {
      setIsLoading(true)
      inquiryCustomPlan(data)
        .then(() => onSubmit?.())
        .catch((err) => {
          setServerError(err)
        })
        .finally(() => setIsLoading(false))
    } else {
      setIsValid(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      desktopStyle={desktopStyle}
      mobileStyle={mobileStyle}
      parentSelector={() => document.getElementById('prompt-modal-page')}
      {...rest}
    >
      <Wrapper>
        <CloseButton
          onClick={onClose}
          styles={{ top: '24px', right: '24px' }}
        />
        <Text
          color={palette.neutral01}
          fontSize="24px"
          fontFamily={fonts.primaryMedium}
          lineHeight="100%"
          fontWeight={500}
        >
          {translate('custom_plan_form_heading')}
        </Text>
        <Text
          color={palette.primaryTint}
          fontSize="14px"
          fontFamily={fonts.primary}
          lineHeight="100%"
          fontWeight={400}
          margin="12px 0 0 0"
        >
          {translate('custom_plan_form_subheading')}
        </Text>
        <Form>
          {serverError && (
            <Row>
              <TableNotification
                type="error"
                content={translate('unexpected_error')}
                onClose={() => setServerError(null)}
                style={{ marginTop: 0 }}
              />
            </Row>
          )}
          <Row>
            <Input
              placeholder={translate('your_name')}
              name="name"
              value={data.name}
              onChange={(e) => {
                setData({ ...data, name: e.target.value })
              }}
            />
            <Input
              placeholder={translate('company_name_required')}
              name="companyName"
              value={data.company_name}
              onChange={(e) => {
                setData({ ...data, company_name: e.target.value })
              }}
            />
          </Row>
          <Row>
            <Input
              placeholder={translate('email_address_required')}
              name="email"
              value={data.email}
              onChange={(e) => {
                setData({ ...data, email: e.target.value })
              }}
              invalidMessage={
                !validateEmail(data.email) &&
                isSubmitted &&
                translate('invalid_email')
              }
            />
            <PhoneInput
              placeholder={translate('phone_optional')}
              name="companyName"
              onChange={(value) => {
                setData({ ...data, phone_number: value })
              }}
              invalidMessage={
                !!data.phone_number &&
                !validatePhoneLib(data.phone_number) &&
                isSubmitted &&
                translate('invalid_phone')
              }
              placeholderColor={palette.primaryTint}
              backgroundColor={palette.lightGray10}
              focusedBorderColor={palette.orange}
              styles={{ flexGrow: 1, flexBasis: 0 }}
            />
          </Row>
          <UberTextarea
            placeholder={translate('specific_requirement')}
            name="message"
            value={data.message}
            onChange={(e) => {
              setData({ ...data, message: e.target.value })
            }}
            height="200px"
            backgroundColor={palette.lightGray10}
            placeholderColor={palette.primaryTint}
            focusedBorderColor={palette.orange}
            style={{ maxWidth: '100%' }}
          />
        </Form>
        <Row>
          <Text
            color={palette.primaryTint}
            fontSize="10px"
            fontFamily={fonts.primary}
            lineHeight="16px"
            fontWeight={400}
            linkColor={palette.primaryTint}
          >
            {reactStringReplace(
              translate('consent_text'),
              /(<a>.*<\/a>)/gi,
              (match, index) => {
                return (
                  <a
                    key={`${match}-${index}`}
                    href="https://neilpatel.com/privacy/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {translate('privacy_policy')}
                  </a>
                )
              },
            )}
          </Text>
          <PrimaryButton
            tag={translate('submit_form')}
            isLoading={isLoading}
            disabled={!isValid}
            theme="orange"
            mobileWidth="100%"
            width="160px"
            height="48px"
            fontSize="16px"
            textTransform="none"
            letterSpacing="normal"
            onClick={handleSubmit}
            style={{ flexShrink: 0 }}
          />
        </Row>
        <Footer>
          <Text
            color={palette.secondaryTint}
            fontSize="12px"
            fontFamily={fonts.primary}
            lineHeight="14.4px"
            fontWeight={400}
            dangerouslySetInnerHTML={{
              __html: translate('rights_reserved', 2025),
            }}
            boldColor={palette.secondaryTint}
          />
        </Footer>
      </Wrapper>
    </Modal>
  )
}
